import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface HyroxProgressionChartProps {
  data: Record<string, unknown>;
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function HyroxProgressionChart({ data }: HyroxProgressionChartProps) {
  const chartData = useMemo(() => {
    const raceTrend = data.race_trend as Array<{ date: string; value: number }> | undefined;
    if (!raceTrend?.length) return [];
    return raceTrend.map((p) => ({
      date: p.date,
      time: Math.round(p.value),
    }));
  }, [data]);

  const plateauDetected = data.plateau as { is_plateau?: boolean; message?: string } | undefined;

  if (!chartData.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Race Time Progression</h3>
        <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
          No race progression data yet. Record multiple Hyrox races to track improvement.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">Race Time Progression</h3>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              width={50}
              tickFormatter={(v: number) => formatTime(v)}
              reversed
            />
            <Tooltip
              contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }}
              formatter={(value: number) => [formatTime(value), "Race Time"]}
            />
            <Line type="monotone" dataKey="time" stroke="hsl(150 55% 50%)" strokeWidth={2} dot={{ r: 3, fill: "hsl(150 55% 50%)" }} name="Race Time" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {plateauDetected?.is_plateau && (
        <div className="text-[10px] font-mono text-amber-400/80 bg-amber-500/10 rounded px-2 py-1.5 border border-amber-500/20 mt-2">
          ⚠ {plateauDetected.message ?? "Race time plateau detected"}
        </div>
      )}
    </div>
  );
}
